<template>
    <aside id="left-sub-sidebar-container" :class="{ 'sub-sidebar-active': isOpen, 'is-document-selected': isDocumentSelected }">
        <!-- Floorplans -->
        <!-- IS MAP -->
        <HeaderHoverUtil
            v-if="type == 'floorplans'"
            :isSectorsSidebarOpen="isSectorsSidebarOpen"
            :isFloorplansSidebarOpen="isFloorplansSidebarOpen"
            :isShowingSubSidebarHeaders="isShowingSubSidebarHeaders"
            @openHeaders="isShowingSubSidebarHeaders = true"
            @closeHeaders="isShowingSubSidebarHeaders = false"
            @showMapSidebar="showMapSidebar"
            @showSectorsSidebar="showSectorsSidebar"
        />

        <MapSidebar
            v-if="type == 'floorplans' && isMap"
            @showMapSidebar="showMapSidebar"
            @mapViewpointClick="mapViewpointClicked"
            @viewpointHover="viewpointHovered"
            @sectorClick="sectorClicked"
            @sectorHover="sectorHovered"
            :project="project"
            :currentSector="currentSector"
            :currentViewpoint="currentViewpoint"
            :selectedSector="selectedSector"
            :selectedViewpoint="selectedViewpoint"
            :hoveredViewpoint="hoveredViewpoint"
        />

        <SectorsSidebar
            v-if="type == 'floorplans' && isMap"
            @floorplanClick="floorplanClicked"
            @floorplanDblClick="floorplanDblClicked"
            @changeSelectedSector="(data) => $emit('changeSelectedSector', data)"
            :isOpen="isSectorsSidebarOpen"
            :project="project"
            :isShowingSubSidebarHeaders="isShowingSubSidebarHeaders"
            :currentSector="currentSector"
            :currentFloorplan="currentFloorplan"
            :selectedSector="selectedSector"
            :selectedFloorplan="selectedFloorplan"
        />

        <FloorplansSidebar
            v-if="type == 'floorplans' && isMap"
            @viewpointClick="viewpointClicked"
            @openFloorplanFullscreen="$emit('openFloorplanFullscreen')"
            @floorplanChange="(floorplan) => (selectedFloorplan = floorplan)"
            @changeSelectedFloorplan="(data) => $emit('changeSelectedFloorplan', data)"
            :isOpen="isMap ? isFloorplansSidebarOpen : true"
            :isMap="isMap"
            :project="project"
            :isShowingSubSidebarHeaders="isShowingSubSidebarHeaders"
            :currentFloorplan="currentFloorplan"
            :currentViewpoint="currentViewpoint"
            :selectedSector="selectedSector"
            :selectedFloorplan="selectedFloorplan"
            :selectedViewpoint="selectedViewpoint"
        />
        <!-- IS MAP END -->

        <!-- IS FLOORPLAN -->
        <NoMapSidebar
            v-if="type == 'floorplans' && !isMap"
            :isOpen="isOpen"
            :currentFloorplan="currentFloorplan"
            :currentViewpoint="currentViewpoint"
            :selectedFloorplan="selectedFloorplan"
            :selectedViewpoint="selectedViewpoint"
            :project="project"
            @viewpointClick="viewpointClicked"
            @openFloorplanFullscreen="$emit('openFloorplanFullscreen')"
            @floorplanChange="(floorplan) => (selectedFloorplan = floorplan)"
            @changeSelectedFloorplan="(data) => $emit('changeSelectedFloorplan', data)"
        />
        <!-- IS FLOORPLAN END -->

        <FloorplansLocationPicker
            :isOpen="isOpen && !isFloorplansSidebarOpen && isMap && type == 'floorplans'"
            :project="project"
            :isMap="isMap"
            :currentSector="currentSector"
            :currentFloorplan="currentFloorplan"
            :currentViewpoint="currentViewpoint"
            :selectedSector="selectedSector"
            :selectedFloorplan="selectedFloorplan"
            :selectedViewpoint="selectedViewpoint"
            :hoveredViewpoint="hoveredViewpoint"
            :hoveredSector="hoveredSector"
            :isMapSidebarOpen="isMapSidebarOpen"
            @sectorClick="sectorClicked"
            @mapViewpointClick="mapViewpointClicked"
            @viewpointClick="
                (viewpoint) => {
                    viewpointClicked(viewpoint)
                    isFloorplansSidebarOpen = true
                }
            "
            @viewpointHover="viewpointHovered"
            @sectorHover="sectorHovered"
        />

        <!-- Issues -->
        <IssuesSidebar
            ref="issues-sidebar"
            v-show="type == 'issues'"
            :is-open="isOpen && type == 'issues'"
            :is-using-shared-link="isUsingSharedLink"
            :project="project"
            :currentFloorplan="currentFloorplan"
            @onNewIssueRequestChangePosition="onNewIssueRequestChangePosition"
            @onRequestCancelNewIssue="onRequestCancelNewIssue"
            @onNewIssueCreated="onNewIssueCreated"
        />

        <div @click="$emit('close', false)" class="back-btn d-flex justify-content-center align-items-center">
            <icon name="chevron-left" scale=".8" color="white" />
        </div>
    </aside>
</template>

<script>
// ====================
// Icons
// ====================
import 'vue-awesome/icons/door-open'
import 'vue-awesome/icons/chevron-left'

// ====================
// UI Components
// ====================

// Floorplans
import NoMapSidebar from '@/components/360Viewer/Sidebar/Floorplans/NoMapSidebar.vue'
import MapSidebar from '@/components/360Viewer/Sidebar/Floorplans/MapSidebar.vue'
import SectorsSidebar from '@/components/360Viewer/Sidebar/Floorplans/SectorsSidebar.vue'
import FloorplansSidebar from '@/components/360Viewer/Sidebar/Floorplans/FloorplansSidebar.vue'
import HeaderHoverUtil from '@/components/360Viewer/Sidebar/Floorplans/HeaderHoverUtil.vue'
import FloorplansLocationPicker from '@/components/360Viewer/Sidebar/Floorplans/FloorplansLocationPicker.vue'

// Issues
import IssuesSidebar from '@/components/360Viewer/Sidebar/Issues/IssuesSidebar.vue'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

/**
 * Summary.
 * Sidebar content container
 * 
 * @param {Boolean} isOpen
 * @param {Boolean} isMap
 * @param {Boolean} isUsingSharedLink
 * 
 * @param {String} type
 * 
 * @param {Object} project
 * @param {Object} currentSector
 * @param {Object} currentFloorplan
 * @param {Object} currentViewpoint
 * 
 * @event changeSubsideBarState
 * @event close

 * @event viewpointClick
 * @event changeSelectedSector(data)
 * @event openFloorplanFullscreen
 * @event changeSelectedFloorplan(data)
 * 
 * @event onNewIssueRequestChangePosition(data)
 * @event onRequestCancelNewIssue
 * @event onNewIssueCreated(issue)
 */

export default {
    props: {
        /**
         * determines if this component is open
         */
        isOpen: Boolean,
        /**
         * determines if project is a map or not
         */
        isMap: Boolean,
        /**
         * determines if user is using a shared link
         */
        isUsingSharedLink: Boolean,
        /**
         * determines sidebar type
         */
        type: String, // floorplans / issues / share
        /**
         * the entire project
         */
        project: {
            type: Object,
            default: () => ({}),
        },
        /**
         * current sector of the showing viewpoint
         */
        currentSector: {
            type: Object,
            default: -1,
        },
        /**
         * current floorplan of the showing viewpoint
         */
        currentFloorplan: {
            type: Object,
            default: -1,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            type: Object,
            default: -1,
        },
    },
    components: {
        // Floorplans
        NoMapSidebar,
        MapSidebar,
        SectorsSidebar,
        FloorplansSidebar,
        HeaderHoverUtil,
        FloorplansLocationPicker,

        // Issues
        IssuesSidebar,
    },
    watch: {
        isOpen() {
            let flag = true
            if (!this.isOpen || this.isFloorplansSidebarOpen) {
                flag = false
            }
            if (this.isMap) this.$emit('changeSubsideBarState', flag)

            if (!this.isOpen) {
                this.$emit('changeSelectedFloorplan', this.currentFloorplan)
            }
        },
        isSectorsSidebarOpen() {
            if (this.isSectorsSidebarOpen) {
                this.selectedFloorplan = {}
            }
        },
        isFloorplansSidebarOpen() {
            let flag = false
            if (this.isFloorplansSidebarOpen) {
                flag = false
            } else {
                flag = true
            }
            if (this.isMap) this.$emit('changeSubsideBarState', flag)
        },
        selectedFloorplan() {
            if (this.selectedFloorplan == undefined || this.selectedFloorplan == -1) {
                this.isFloorplansSidebarOpen = false
            }
        },
    },
    computed: {
        isMapSidebarOpen() {
            if (this.isOpen && !this.isSectorsSidebarOpen && !this.isFloorplansSidebarOpen) {
                if (this.currentSector && this.currentSector.id && this.currentFloorplan && this.currentViewpoint) {
                    setTimeout(() => {
                        this.selectedSector = -1
                    }, 0)
                }
            }
            return this.isOpen && !this.isSectorsSidebarOpen && !this.isFloorplansSidebarOpen
        },
        ...mapState(['dateFilterViewer']),
        ...mapGetters(['getDatesByProjectId']),
    },
    data() {
        return {
            // Floorplans
            isSectorsSidebarOpen: false,
            isFloorplansSidebarOpen: false,
            isShowingSubSidebarHeaders: false,
            selectedSector: {},
            selectedFloorplan: {},
            selectedViewpoint: {},
            hoveredViewpoint: {},
            hoveredSector: {},
            isFloorplanFullscreenOpen: false,
            isDocumentSelected: false,
        }
    },
    methods: {
        ...mapActions('dateFilterViewer', ['setDates']),
        showMapSidebar() {
            if (this.isMap) {
                this.isSectorsSidebarOpen = false
                this.isFloorplansSidebarOpen = false
                this.isShowingSubSidebarHeaders = false
                // reset selected
                this.resetSelectedOnNavigation()
            }
        },
        showSectorsSidebar() {
            this.isFloorplansSidebarOpen = false
            this.isShowingSubSidebarHeaders = false
            // reset selected
            this.resetSelectedOnNavigation()
        },
        sectorClicked(sector) {
            this.isSectorsSidebarOpen = true
            this.selectedSector = sector
            this.$emit('sectorClick', sector)
        },
        sectorHovered(sector) {
            this.hoveredSector = sector
        },
        // Viewpoints
        mapViewpointClicked(viewpoint) {
            this.selectedSector = -1
            this.selectedViewpoint = viewpoint

            this.selectedFloorplan = {
                id: this.project.map.id,
                name: this.project.name,
                url: this.project.map.url,
                viewpoints: this.project.map.viewpoints,
                sectors: this.project.map.sectors,
            }
            this.$emit('viewpointClick', { fp: this.selectedFloorplan, vp: viewpoint })
        },
        viewpointClicked(viewpoint) {
            this.selectedViewpoint = viewpoint
            this.$emit('viewpointClick', { fp: this.selectedFloorplan, vp: viewpoint })
        },
        viewpointHovered(viewpoint) {
            this.hoveredViewpoint = viewpoint
        },
        // Floorplans
        floorplanClicked(floorplan) {
            this.selectedFloorplan = floorplan
            this.$emit('changeSelectedFloorplan', floorplan)
        },
        floorplanDblClicked(floorplan) {
            this.isFloorplansSidebarOpen = true
            this.selectedFloorplan = floorplan
        },
        resetSelectedOnNavigation() {
            // // TODO fix logic
            if (this.isMapSidebarOpen) {
                this.selectedSector = {}
                this.selectedFloorplan = {}
            }
            // this.selectedFloorplan = {}
            // this.selectedViewpoint = {}
        },
        // Issues
        onNewIssueRequestChangePosition(data) {
            this.$emit('onNewIssueRequestChangePosition', data)
        },
        onRequestCancelNewIssue() {
            this.$emit('onRequestCancelNewIssue')
        },
        onNewIssueCreated(issue) {
            this.$emit('onNewIssueCreated', issue)
        },
        setupSelectedFilterDates() {
            if (!this.$route.query['disable-filter-reset']) {
                this.setDates({ projectId: this.project.id, dates: [] })
            }
        },
        // Construcode
        onDocumentToggled(data) {
            this.isDocumentSelected = data
            this.$emit('onDocumentToggled', data)
        },

        requireExpandConstrucodeDocument(file, area) {
            this.$emit('requireExpandConstrucodeDocument', file, area)
        },
    },
    mounted() {
        this.setupSelectedFilterDates()
        if (!this.isMap) {
            this.selectedFloorplan = this.currentFloorplan
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/left-sub-sidebar-container.scss';
</style>
